import { Cross2Icon, DesktopIcon, MobileIcon, ReaderIcon } from '@radix-ui/react-icons'
import { Button } from '@radix-ui/themes'
import { Alert, AlertType, ScanMethod, Severity } from '../../types/AlertTypes'
import { Threat } from '../../types/ActivityTypes'
import { selectUser } from '../../reducers/users'
import { useSelector } from 'react-redux'
import { RoleType } from '../../types/UserTypes'
import { getRoles, permitsRole } from '../../utils/roles'
import { IconExclamationCircle } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import SideBarSkeleton from '../skeleton/SideBarSkeleton'
import { fetchDetailsForAlert } from '../../reducers/alerts'
import { formatUtcTimestamp } from '../../utils/dates'

interface IAlertPopoutProps {
  popoutData: string
  setShowPopout: (show: boolean) => void
  setShowResolveModal: (show: boolean) => void
}

export const AlertPopoutContent = (props: IAlertPopoutProps) => {
  const di = useAppDispatch()
  const { popoutData, setShowPopout, setShowResolveModal } = props
  const { session } = useSelector(selectUser)
  const [fetchedAlertDetails, setFetchedAlertDetails] = useState<Alert | undefined>(undefined)
  const roles = getRoles(session)

  useEffect(() => {
    fetchAlertDetails(popoutData)
  }, [])

  const fetchAlertDetails = async (alertId: string) => {
    const fetchedDetails = await di(fetchDetailsForAlert(alertId))
    setFetchedAlertDetails(fetchedDetails)
  }

  const handleScanMethod = (scanMethod: string) => {
    switch (scanMethod) {
      case ScanMethod.IVY_APP:
        return (
          <div className="flex">
            <MobileIcon className="mt-0.5 mr-2" />
            <p>Device Check In</p>
          </div>
        )
      case ScanMethod.THREAT_HUNTER:
        return (
          <div className="flex">
            <DesktopIcon className="mt-0.5 mr-2" />
            <p>Threat Hunter</p>
          </div>
        )
      case ScanMethod.APP_SYSDIAGNOSE:
        return (
          <div className="flex">
            <ReaderIcon className="mt-0.5 mr-2" />
            <p>Sysdiagnose</p>
          </div>
        )
      case ScanMethod.APP_BUG_REPORT:
        return (
          <div className="flex">
            <ReaderIcon className="mt-0.5 mr-2" />
            <p>Bug Report</p>
          </div>
        )
      case ScanMethod.IVERIFY_ELITE:
        return (
          <div className="flex">
            <MobileIcon className="mt-0.5 mr-2" />
            <p>Extended Protection</p>
          </div>
        )
      default:
        return null
    }
  }

  const handleAlertType = (scanMethod: string) => {
    switch (scanMethod) {
      case AlertType.TRAVEL:
        return 'Travel'
      case AlertType.DEVICE_SCAN:
      default:
        return 'Device'
    }
  }

  const generateHeaderColour = (severity: string) => {
    switch (severity) {
      case Severity.CRITICAL:
        return 'bg-criticalBg text-criticalText'
      case Severity.HIGH:
        return 'bg-highBg text-highText'
      case Severity.SUSPICIOUS:
        return 'bg-highBg text-highText'
      case Severity.LOW:
        return 'bg-lowBg text-lowText'
      default:
        return 'bg-criticalBg text-criticalText'
    }
  }

  const generateHeaderIconFill = (severity: string) => {
    switch (severity) {
      case Severity.CRITICAL:
        return 'text-criticalText'
      case Severity.HIGH:
        return 'text-highText'
      case Severity.SUSPICIOUS:
        return 'text-highText'
      case Severity.LOW:
        return 'text-lowText'
      default:
        return 'text-criticalText'
    }
  }

  const checkIfUpdated = (updatedAt: string, createdAt: string) => {
    return updatedAt ? formatUtcTimestamp(updatedAt, false, true) : formatUtcTimestamp(createdAt, false, true)
  }

  return (
    <>
      {!fetchedAlertDetails && <SideBarSkeleton />}
      {fetchedAlertDetails && (
        <>
          <div
            className={`w-full h-[52px] flex justify-center items-center border-mainBorder border-b ${generateHeaderColour(fetchedAlertDetails.severity)}`}
          >
            <IconExclamationCircle className={generateHeaderIconFill(fetchedAlertDetails.severity)} size={20} />
            <p className="text-xs font-semibold ml-1">
              This alert is rated as {fetchedAlertDetails.severity?.toLowerCase()}
            </p>
          </div>
          {/* Name */}
          <div className="w-full">
            <div className="w-full min-h-20 border-mainBorder border-b flex gap-2 px-6">
              <div className="flex grow flex-col justify-center">
                <div className="text-mainText text-lg font-semibold leading-normal">
                  {fetchedAlertDetails.memberName}
                </div>
                <div className="text-mainText text-opacity-50 text-sm font-semibold leading-normal">
                  {fetchedAlertDetails.device}
                </div>
              </div>
              <div className="h-20 flex justify-end items-center">
                <div
                  className="w-6 h-6 cursor-pointer"
                  onClick={() => {
                    setShowPopout(false)
                  }}
                >
                  <Cross2Icon className="w-6 h-6" />
                </div>
              </div>
            </div>
            {/* Alert Name */}
            <div className="w-full border-mainBorder border-b pb-4 px-8">
              <div className="flex">
                <div className="w-1/2 h-12 flex flex-col justify-center">
                  <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">
                    Alert Details
                  </div>
                </div>
              </div>
              <div className="w-full mt-2">
                {JSON.parse(fetchedAlertDetails?.alertDescription)?.map((alert: Threat, idx: number) => {
                  // Regex to find hash after 'Hash: ' and before '.'
                  const hashMatch = alert.threat.match(/Hash:\s*([a-fA-F0-9]+)\./)
                  if (hashMatch) {
                    const hash = hashMatch[1]
                    const virusTotalUrl = `https://virustotal.com/gui/file/${hash}`
                    const parts = alert.threat.split(hash)
                    return (
                      <p className="text-sm" key={idx}>
                        • {parts[0]}
                        <a
                          href={virusTotalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-mainPurple dark:text-secondaryPurple underline break-all"
                        >
                          {hash}
                        </a>
                        {parts[1]}
                      </p>
                    )
                  } else {
                    return (
                      <p className="text-sm" key={idx}>
                        • {alert.threat}
                      </p>
                    )
                  }
                })}
              </div>
            </div>
            {/* Alert Created */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/3 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">First Seen</div>
              </div>
              <div className="w-2/3 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {formatUtcTimestamp(fetchedAlertDetails.createdAt, false, true)}
                </div>
              </div>
            </div>
            {/* Alert Updated */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/3 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Last Seen</div>
              </div>
              <div className="w-2/3 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {checkIfUpdated(fetchedAlertDetails.updatedAt, fetchedAlertDetails.createdAt)}
                </div>
              </div>
            </div>
            {/* Status */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Alert Status</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {fetchedAlertDetails.alertStatus}
                </div>
              </div>
            </div>
            {/* Type */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Alert Type</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {handleAlertType(fetchedAlertDetails.alertType)}
                </div>
              </div>
            </div>
            {/* Scanned by */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Access Code</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {fetchedAlertDetails.scannedByAccessCode}
                </div>
              </div>
            </div>
            {/* Scan method */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Scan Type</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {handleScanMethod(fetchedAlertDetails.scanMethod)}
                </div>
              </div>
            </div>
            {/* Platform */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Platform</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {fetchedAlertDetails.platform}
                </div>
              </div>
            </div>
            {/* OS */}
            <div className="w-full h-12 border-mainBorder border-b flex px-8">
              <div className="w-1/2 h-12 flex flex-col justify-center">
                <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">OS</div>
              </div>
              <div className="w-1/2 h-12 flex justify-end items-center float-right">
                <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                  {fetchedAlertDetails.osVersion}
                </div>
              </div>
            </div>
            {/* Scanned By */}
            {fetchedAlertDetails.scanMethod === ScanMethod.THREAT_HUNTER && (
              <div className="w-full h-12 border-mainBorder border-b flex px-8">
                <div className="w-1/2 h-12 flex flex-col justify-center">
                  <div className="text-mainText text-sm font-normal font-['Open Sans'] leading-normal">Scanned By</div>
                </div>
                <div className="w-1/2 h-12 flex justify-end items-center float-right">
                  <div className="text-mainText text-sm font-semibold font-['Open Sans'] leading-normal">
                    {fetchedAlertDetails.scannedBy}
                  </div>
                </div>
              </div>
            )}
            {permitsRole(roles, [RoleType.ACCESS_MGMT]) && (
              <div className="h-20 flex items-center mt-4">
                {fetchedAlertDetails.alertStatus === 'OPEN' && (
                  <Button
                    className="w-[125px] h-10 bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple my-5 mr-8 ml-auto cursor-pointer"
                    onClick={() => setShowResolveModal(true)}
                  >
                    Resolve
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
